import { z } from 'zod';

// -----------------------------------------------------------------------------
// whoami
// -----------------------------------------------------------------------------

export const platformWhoamiSchema = z.object({
  platformUser: z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    name: z.string(),
    platformRole: z.string(),
  }),
  impersonating: z.boolean(),
  impersonatedUserId: z.string().uuid().nullable(),
  impersonatedUserName: z.string().nullable(),
  impersonatedOrganizationId: z.string().uuid().nullable(),
  impersonatedOrganizationName: z.string().nullable(),
  // ISO 8601 when set; null when `IMPERSONATION_TIMEBOX_HOURS=0`.
  impersonationExpiresAt: z.string().nullable(),
});
export type PlatformWhoami = z.infer<typeof platformWhoamiSchema>;

// -----------------------------------------------------------------------------
// organizations list — paginated + search
// -----------------------------------------------------------------------------

export const platformOrganizationSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  // ISO 8601 string. Hono serializes Date → string; the client parses as string.
  createdAt: z.string(),
  activeUserCount: z.number().int().nonnegative(),
});
export type PlatformOrganization = z.infer<typeof platformOrganizationSchema>;

export const platformOrganizationsRequestSchema = z.object({
  // Matches org name, or any active user's email / first_name / last_name.
  q: z.string().optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(50),
});
export type PlatformOrganizationsRequest = z.input<typeof platformOrganizationsRequestSchema>;

export const platformOrganizationsResponseSchema = z.object({
  data: z.array(platformOrganizationSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().min(1),
  pageSize: z.number().int().min(1),
  totalPages: z.number().int().min(1),
});
export type PlatformOrganizationsResponse = z.infer<typeof platformOrganizationsResponseSchema>;

// -----------------------------------------------------------------------------
// org users list (impersonation picker) — paginated + search
// -----------------------------------------------------------------------------

export const platformOrgUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  firstName: z.string(),
  lastName: z.string(),
});
export type PlatformOrgUser = z.infer<typeof platformOrgUserSchema>;

// -----------------------------------------------------------------------------
// platform_users admin (CRUD on the VagusX-side directory)
// -----------------------------------------------------------------------------

export const platformUserSchema = z.object({
  id: z.string().uuid(),
  baUserId: z.string(),
  email: z.string().email(),
  name: z.string(),
  platformRole: z.string(),
  rowStatus: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PlatformUser = z.infer<typeof platformUserSchema>;

export const createPlatformUserSchema = z.object({
  email: z.string().email(),
  name: z.string().min(1),
  platformRole: z.literal('platform_admin'),
});
export type CreatePlatformUserRequest = z.infer<typeof createPlatformUserSchema>;

// -----------------------------------------------------------------------------
// impersonation lifecycle
// -----------------------------------------------------------------------------

export const startImpersonationSchema = z.object({
  organizationId: z.string().uuid(),
  targetUserId: z.string().uuid(),
});
export type StartImpersonationRequest = z.infer<typeof startImpersonationSchema>;

export const startImpersonationResponseSchema = z.object({
  session: z.object({
    impersonating: z.boolean(),
    impersonatedUserId: z.string().uuid().nullable(),
    impersonatedOrganizationId: z.string().uuid().nullable(),
    impersonationExpiresAt: z.string().nullable(),
  }),
  switchedFrom: z
    .object({ userId: z.string().uuid(), organizationId: z.string().uuid() })
    .nullable(),
  redirectTo: z.string(),
});
export type StartImpersonationResponse = z.infer<typeof startImpersonationResponseSchema>;

export const stopImpersonationResponseSchema = z.object({
  stopped: z.boolean(),
  redirectTo: z.string(),
});
export type StopImpersonationResponse = z.infer<typeof stopImpersonationResponseSchema>;

// -----------------------------------------------------------------------------
// misc
// -----------------------------------------------------------------------------

export const platformConfigSchema = z.object({
  impersonationTimeboxHours: z.number().int().nonnegative(),
});
export type PlatformConfig = z.infer<typeof platformConfigSchema>;

/**
 * `X-Impersonation: 1` is a presence marker only — the SPA should fetch
 * `/api/platform/whoami` for the authoritative impersonation state. The
 * header deliberately carries no identifiers to avoid leaking UUIDs
 * through log aggregators, APMs, or shared-layer monitoring.
 */
export function isImpersonationHeaderPresent(raw: string | null | undefined): boolean {
  return raw === '1';
}
